'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { JobPortalOffer } from '@eliadmin/core/src/api/jobportal/offers-api';
//import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { CategoryBadge } from './card/category-badge';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { AddToFavourite } from './card/add-to-favorites';
import { format } from 'date-fns';

import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { RiMapPin2Line } from 'react-icons/ri';
import { formatNumber } from './jobportal-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export interface OfferCardProps {
  offer: JobPortalOffer;
  bottomBorder?: boolean;
  favoriteButton?: boolean;
}

export const JobOfferCard = ({
  offer,
  bottomBorder = false,
  favoriteButton = false,
}: OfferCardProps) => {
  const currency = useGlobalStore(s => s.generalLayout.currency);

  const me = useCustomerStore(s => s.me);
  const showFavoriteButton = me ? true : false;

  return (
    <div
      className={cn(
        'ea-job-offer-card__main pl-4 md:pl-6',
        'relative flex w-full items-start justify-start gap-4 py-6 md:items-center md:justify-between md:rounded-[24px] md:px-6 lg:gap-8',
        {
          'border-b border-b-[#D8D8DB] md:border-none': bottomBorder,
        },
      )}
    >
      {showFavoriteButton && (
        <AddToFavourite
          offer={offer}
          type="offer"
          className={{ container: 'absolute right-6 top-6 z-[10] h-9 w-9' }}
        />
      )}
      <Link
        prefetch={false}
        href={`${offer.slug}`}
        className="relative min-h-16 min-w-16 overflow-hidden rounded-[12px] md:min-h-24 md:min-h-[140px] md:min-w-24 md:min-w-[140px] md:rounded-[24px] lg:min-h-[180px] lg:min-w-[180px]"
      >
        <Image
          src={offer?.company?.image?.url || '/images/placeholder.png'}
          alt={resolveImageAlt(offer?.company?.image, offer?.name)}
          style={{ objectFit: 'cover', objectPosition: 'center' }}
          fill
          sizes="180px"
        />
      </Link>
      <DesktopLayout offer={offer} currency={currency} />
      <MobileLayout offer={offer} currency={currency} />
    </div>
  );
};

const getOfferSalary = (
  offer: JobPortalOffer,
  t: (key: string) => string,
  currency: string,
): string => {
  if (offer?.min_salary && offer?.max_salary && parseFloat(offer?.max_salary) > 0) {
    if (offer.min_salary === offer.max_salary) {
      return `${formatNumber(offer.min_salary)}${currency}`;
    }

    return `${formatNumber(offer.min_salary)} - ${formatNumber(offer.max_salary)}${currency}`;
  }

  if (offer?.min_salary) {
    return `${t('text811')} ${formatNumber(offer.min_salary)}${currency}`;
  }

  return t('text935');
};

const DesktopLayout = ({ offer, currency }: { offer: JobPortalOffer; currency: string }) => {
  //TODO: add created at, last edit date etc...
  const { t } = useTranslations();

  const data = [
    {
      label: '',
      value: offer.employment_type?.name,
    },
    {
      label: 'Plat',
      value: getOfferSalary(offer, t, currency),
    },
    {
      label: 'Vytvorené',
      value: offer.published_at ? format(new Date(offer.published_at), 'dd.MM.yyyy') : '---',
    },
  ];

  const isDualEducation = offer?.tags?.some(tag => tag.id === 4);

  const isTopped = !!offer?.topped_up_to && new Date(offer.topped_up_to).getTime() > Date.now();

  return (
    <div className="hidden w-full items-center justify-between gap-6 md:flex">
      <div className="flex w-full max-w-[740px] flex-col items-start justify-start gap-2">
        <div className="flex w-full flex-wrap items-center justify-start gap-2 md:gap-4">
          {isDualEducation && (
            <Link
              href={'/dualne-vzdelavanie'}
              className="absolute right-0 top-0 z-[10] flex h-[30px] w-[170px] items-center justify-center whitespace-nowrap rounded-bl-lg rounded-tr-[24px] bg-primary text-sm font-bold text-white"
            >
              {t('Duálne vzdelávanie')}
            </Link>
          )}
          {offer.categories.map(category => (
            <CategoryBadge key={category.id} category={category} />
          ))}
          <div className="flex items-center justify-start gap-[2px] font-medium text-primary">
            {(offer.city || offer.country) && <RiMapPin2Line size={20} className="pb-1" />}
            {offer.city && (
              <p className=" text-xs">
                {offer.city}, <span className="uppercase">{offer.country?.id || ''}</span>
              </p>
            )}
            {!offer.city && offer.country?.name && (
              <p className=" text-xs">
                <span className="uppercase">{offer.country?.name || ''}</span>
              </p>
            )}
          </div>
        </div>
        <Link prefetch={false} href={`${offer.slug}`}>
          <h3 className="w-full text-sm font-bold text-foreground md:text-base md:text-lg lg:text-xl xl:text-2xl">
            {offer.name}
          </h3>
        </Link>
        <Link
          prefetch={false}
          href={`${offer.slug}`}
          className="text-xs font-normal text-muted-foreground md:text-sm"
        >
          {offer.company?.name}
        </Link>
      </div>
      <ul className="flex w-fit flex-col gap-2 border-l border-l-[#D8D8DB] py-3 pl-4 lg:pl-8">
        {data.map(item => (
          <li key={item.label}>
            <Link
              prefetch={false}
              href={`${offer.slug}`}
              className={cn('flex items-center justify-start gap-2')}
            >
              {!!item.label && (
                <p className="text-sm font-normal text-muted-foreground lg:text-base">
                  {item.label + ':'}
                </p>
              )}
              <span className="text-sm font-bold text-foreground lg:text-base">{item.value}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

const MobileLayout = ({ offer, currency }: { offer: JobPortalOffer; currency: string }) => {
  const { t } = useTranslations();
  const data = [
    {
      label: '',
      value: offer.employment_type?.name || null,
    },
    {
      label: '',
      value: getOfferSalary(offer, t, currency),
    },
    {
      label: t('text1788'),
      value: offer.published_at ? format(new Date(offer.published_at), 'dd.MM.yyyy') : '---',
    },
  ];

  return (
    <div className="flex flex-col items-start justify-start gap-2 md:hidden">
      <Link prefetch={false} href={`${offer.slug}`}>
        <h3 className="w-full pr-10 text-sm font-bold text-foreground md:pr-0 md:text-base md:text-lg lg:text-xl xl:text-2xl">
          {offer.name}
        </h3>
      </Link>
      <Link
        prefetch={false}
        href={`${offer.slug}`}
        className="text-xs font-normal text-muted-foreground md:text-sm"
      >
        {offer.company?.name}
      </Link>
      <div className="flex items-center gap-1 text-primary">
        {(offer.city || offer.country) && <RiMapPin2Line size={20} className="pb-1" />}
        {offer.city && (
          <p className=" text-xs">
            {offer.city}, <span className="uppercase">{offer.country?.id || ''}</span>
          </p>
        )}
        {!offer.city && offer.country?.name && (
          <p className=" text-xs">
            <span className="uppercase">{offer.country?.name || ''}</span>
          </p>
        )}
      </div>
      <div className="flex w-full flex-wrap items-start justify-start gap-2">
        {offer.categories.map(category => (
          <CategoryBadge key={category.id} category={category} />
        ))}
      </div>
      <div className="flex items-center justify-start gap-1 text-sm font-normal text-muted-foreground">
        {data.map(
          (item, index) =>
            (item.label || item.value) && (
              <>
                <div className="flex gap-[6px] whitespace-nowrap">
                  {item.label && (
                    <span className="hidden whitespace-nowrap sm:block">{item.label}</span>
                  )}{' '}
                  <span className="whitespace-nowrap">{item.value}</span>
                </div>
                {index !== data.length - 1 && <span className="text-muted-foreground">/</span>}
              </>
            ),
        )}
      </div>
    </div>
  );
};
